import { trackPixel } from '@/lib/metaPixel'
import { createRouter, createWebHistory } from 'vue-router'
import { isMenuPublicSite } from '@/lib/appShell'
import { getPublicMenuBaseUrl, getLmenusPublicBaseUrl } from '@/lib/restaurantPublicUrl'
import { getAdminBaseUrl, getLmenusLoginUrl, isGustoHost } from '@/lib/appShell'
import { OTHER_PUBLIC_ENTITY_CONFIGS } from '@/lib/entityPublicUrl'
import { clearPanelSession, validatePanelSession } from '@/lib/panelSession'
import { isLmenusOwnerSession } from '@/lib/lmenusSlug'
import { currentPosRole, posCan, posHomePath } from '@/lib/posPermissions'
import { homeFor, loginFor, ownerHome } from '@/lib/panelRoutes'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: () => import('../Layouts/MenuPublicLayout.vue'),
      children: [
        {
          path: '',
          name: 'MenuHome',
          component: () => import('../views/MenuHomeView.vue'),
          meta: { public: true, menuSiteHome: true }
        },
        {
          path: 'cart',
          name: 'MenuCart',
          component: () => import('../views/MenuCartView.vue'),
          meta: { public: true, menuRestaurantFlow: true }
        },
        {
          path: 'cart/checkout',
          name: 'MenuCheckout',
          component: () => import('../views/MenuCheckoutView.vue'),
          meta: { public: true, menuRestaurantFlow: true }
        },
        {
          path: 'order/success',
          name: 'MenuOrderSuccess',
          component: () => import('../views/MenuOrderSuccessView.vue'),
          meta: { public: true, menuRestaurantFlow: true }
        },
        {
          path: 'account',
          name: 'MenuAccount',
          component: () => import('../views/MenuAccountView.vue'),
          meta: { public: true, menuLightFlow: true }
        },
        {
          path: 'r/:restaurantId',
          name: 'RestaurantPublic',
          component: () => import('../views/RestaurantPublicView.vue'),
          meta: { public: true, menuRestaurantFlow: true }
        },
        {
          path: 'm/:slug',
          name: 'LMenusRestaurantPublic',
          component: () => import('../views/RestaurantPublicView.vue'),
          meta: { public: true, menuRestaurantFlow: true, lmenusPublic: true }
        },
        {
          path: 'p/:entityId',
          name: 'PharmacyPublic',
          component: () => import('../views/PharmacyPublicView.vue'),
          meta: { public: true, menuEntityFlow: true }
        },
        {
          path: 'shop/:entityId',
          name: 'ShopPublic',
          component: () => import('../views/ShopPublicView.vue'),
          meta: { public: true, menuEntityFlow: true }
        },
        {
          path: 'lab/:entityId',
          name: 'LabPublic',
          component: () => import('../views/LabPublicView.vue'),
          meta: { public: true, menuEntityFlow: true }
        },
        {
          path: 'p/:entityId/order',
          redirect: (to) => ({
            name: 'PharmacyPublic',
            params: { entityId: to.params.entityId }
          })
        },
        {
          path: 'shop/:entityId/order',
          redirect: (to) => ({
            name: 'ShopPublic',
            params: { entityId: to.params.entityId }
          })
        },
        {
          path: 'lab/:entityId/order',
          redirect: (to) => ({
            name: 'LabPublic',
            params: { entityId: to.params.entityId }
          })
        },
        {
          path: 'r/:restaurantId/checkout',
          redirect: (to) => ({
            path: '/cart/checkout',
            query: to.params.restaurantId ? { restaurantId: to.params.restaurantId } : {}
          })
        }
      ]
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('../views/LoginView.vue'),
      meta: { ui: 'v2' }
    },
    {
      path: '/pos/login',
      name: 'PosLogin',
      component: () => import('../views/pos/PosLoginView.vue'),
      meta: { ui: 'v2' }
    },
    {
      path: '/pos',
      component: () => import('../Layouts/PosLayout.vue'),
      meta: { ui: 'v2' },
      children: [
        { path: '', name: 'PosHome', redirect: () => posHomePath() },
        { path: 'sell', name: 'PosSell', component: () => import('../views/pos/PosCashierView.vue'), meta: { pos: true, posPerm: 'sell' } },
        { path: 'tables', name: 'PosTables', component: () => import('../views/pos/PosTablesView.vue'), meta: { pos: true, posPerm: 'tables' } },
        { path: 'kitchen', name: 'PosKitchen', component: () => import('../views/pos/PosKitchenView.vue'), meta: { pos: true, posPerm: 'kitchen' } },
        { path: 'orders', name: 'PosOrders', component: () => import('../views/pos/PosOrdersView.vue'), meta: { pos: true, posPerm: 'orders' } },
        { path: 'shift', name: 'PosShift', component: () => import('../views/pos/PosShiftView.vue'), meta: { pos: true, posPerm: 'shift' } },
        { path: 'staff', name: 'PosManageStaff', component: () => import('../views/pos/PosStaffView.vue'), meta: { pos: true, posPerm: 'staff' } },
        { path: 'reports', name: 'PosManageReports', component: () => import('../views/pos/PosReportsView.vue'), meta: { pos: true, posPerm: 'reports' } },
        { path: 'settings', name: 'PosManageSettings', component: () => import('../views/pos/PosSettingsView.vue'), meta: { pos: true, posPerm: 'settings' } }
      ]
    },
    {
      path: '/privacypolicy',
      name: 'PrivacyPolicy',
      component: () => import('../views/PrivacyPolicyView.vue'),
      meta: { public: true }
    },
    {
      // The product is Gusto! (November 2026, was Restora, before that LMenus).
      // Old /lmenus and /restora links from posts, ads and bios keep working
      // and land on the same page under /gusto (firebase.json also 301s them).
      path: '/lmenus/:rest(.*)*',
      redirect: (to) => ({
        path: `/gusto${to.params.rest && to.params.rest.length ? '/' + [].concat(to.params.rest).join('/') : ''}`,
        query: to.query,
        hash: to.hash
      })
    },
    {
      path: '/restora/:rest(.*)*',
      redirect: (to) => ({
        path: `/gusto${to.params.rest && to.params.rest.length ? '/' + [].concat(to.params.rest).join('/') : ''}`,
        query: to.query,
        hash: to.hash
      })
    },
    {
      path: '/gusto',
      component: () => import('../Layouts/LMenusPublicLayout.vue'),
      children: [
        {
          path: '',
          name: 'LMenusLanding',
          component: () => import('../views/LMenusLandingView.vue'),
          meta: { public: true, lmenusMarketing: true }
        },
        {
          path: 'register',
          name: 'LMenusRegister',
          component: () => import('../views/LMenusRegisterView.vue'),
          meta: { public: true, lmenusMarketing: true, ui: 'v2' }
        },
        {
          path: 'login',
          name: 'LMenusLogin',
          component: () => import('../views/LMenusLoginView.vue'),
          meta: { public: true, lmenusMarketing: true, ui: 'v2' }
        }
      ]
    },
    {
      // Needs an explicit path: without one this layout itself matches "/"
      // ahead of MenuHome, and the menu site redirects "/" to "/" forever.
      path: '/',
      component: () => import('../Layouts/ManagmentLayout.vue'),
      children: [
        {
          path: '/reviews-analysis',
          name: 'ReviewsAnalysis',
          component: () => import('../views/ReviewsAnalysisView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/Order',
          name: 'Orders',
          component: () => import('../views/OrderView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin', 'office'] }
        },
        // {
        //   path: '/Nursing',
        //   name: 'Nursings',
        //   component: () => import('../views/NursingView.vue')
        // },
        // {
        //   path: '/Getme',
        //   name: 'Getme',
        //   component: () => import('../views/GetmeView.vue')
        // },
        {
          path: '/Resturant',
          name: 'Resturants',
          component: () => import('../views/RestaurantView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'], categoryAdminKind: 'restaurant' }
        },
        {
          path: '/Resturant/:id/items',
          name: 'RestaurantItems',
          component: () => import('../views/RestaurantItemsView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'], categoryAdminKind: 'restaurant' }
        },
        {
          path: '/Coffeeshop',
          name: 'Coffeeshops',
          component: () => import('../views/RestaurantView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'], categoryAdminKind: 'cafe' }
        },
        {
          path: '/Coffeeshop/:id/items',
          name: 'CafeItems',
          component: () => import('../views/RestaurantItemsView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'], categoryAdminKind: 'cafe' }
        },
        {
          path: '/Store',
          name: 'Stores',
          component: () => import('../views/StoreView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/Pharmacy',
          name: 'Pharmacies',
          component: () => import('../views/PharmacyView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/Market',
          name: 'Markets',
          component: () => import('../views/RestaurantView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'], categoryAdminKind: 'market' }
        },
        {
          path: '/Market/:id/items',
          name: 'MarketItems',
          component: () => import('../views/RestaurantItemsView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'], categoryAdminKind: 'market' }
        },
        {
          path: '/Lab',
          name: 'Labs',
          component: () => import('../views/LabView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/Doctor',
          name: 'Doctors',
          component: () => import('../views/DoctorView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        // {
        //   path: '/Craftsman',
        //   name: 'Craftsman',
        //   component: () => import('../views/CraftsmanView.vue'),
        //   meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        // },
        // {
        //   path: '/Teacher',
        //   name: 'Teachers',
        //   component: () => import('../views/TeachersView.vue'),
        //   meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        // },
        // {
        //   path: '/Realestate',
        //   name: 'Realestate',
        //   component: () => import('../views/RealestateView.vue'),
        //   meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        // },
        // {
        //   path: '/Job',
        //   name: 'Jobs',
        //   component: () => import('../views/JobView.vue'),
        //   meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        // },
        // {
        //   path: '/News',
        //   name: 'News',
        //   component: () => import('../views/NewsView.vue'),
        //   meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        // },
        {
          path: '/Subscriptions',
          name: 'Subscriptions',
          component: () => import('../views/SubscriptionsView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/Delivery',
          name: 'Delivery',
          component: () => import('../views/DeliveryZonesView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin', 'office'] }
        },
        {
          path: '/Captain',
          name: 'Captains',
          component: () => import('../views/DeliveryMenView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin', 'office'] }
        },
        {
          path: '/office/service-fee',
          name: 'OfficeServiceFee',
          component: () => import('../views/OfficeServiceFeeView.vue'),
          meta: { ui: 'v2', roles: ['office', 'admin'] }
        },
        {
          path: '/office/captain-settlement',
          name: 'CaptainSettlement',
          component: () => import('../views/CaptainSettlementView.vue'),
          meta: { ui: 'v2', roles: ['office', 'admin'] }
        },
        {
          path: '/Banner',
          name: 'Banners',
          component: () => import('../views/BannersView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/offers',
          name: 'OffersAdmin',
          component: () => import('../views/OffersView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/Analytics',
          name: 'Analytics',
          component: () => import('../views/AnalyticsView.vue'),
          meta: { ui: 'v2', roles: ['super_admin'] }
        },
        {
          path: '/entity-reviews',
          name: 'EntityReviewsAdmin',
          component: () => import('../views/EntityReviewsAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/entity-owner-accounts',
          name: 'EntityOwnerAccounts',
          component: () => import('../views/EntityOwnerAccountsView.vue'),
          meta: { ui: 'v2', roles: ['super_admin'] }
        },
        {
          path: '/regions-visibility',
          name: 'RegionsVisibility',
          component: () => import('../views/RegionsVisibilityView.vue'),
          meta: { ui: 'v2', roles: ['super_admin'] }
        },
        {
          path: '/city-collection/:regionId/:cityIndex',
          name: 'CityCollection',
          component: () => import('../views/CityCollectionView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/credit-pricing',
          name: 'CreditPricingAdmin',
          component: () => import('../views/CreditPricingAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin'] }
        },
        {
          path: '/app-settings',
          name: 'AppSettings',
          component: () => import('../views/AppSettingsView.vue'),
          meta: { ui: 'v2', roles: ['super_admin'] }
        },
        {
          path: '/app-types',
          name: 'AppTypesAdmin',
          component: () => import('../views/AppTypesAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin'] }
        },
        {
          path: '/gusto-admin',
          name: 'LMenusAdmin',
          component: () => import('../views/LMenusAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin'] }
        },
        { path: '/lmenus-admin', redirect: '/gusto-admin' },
        {
          path: '/menu-customers',
          name: 'MenuCustomersAdmin',
          component: () => import('../views/MenuCustomersAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin'] }
        },
        {
          path: '/agent-conversations',
          name: 'AgentConversationsAdmin',
          component: () => import('../views/AgentConversationsAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin'] }
        },
        {
          path: '/coupons',
          name: 'CouponsAdmin',
          component: () => import('../views/CouponsAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin'] }
        },
        {
          path: '/credit-topup',
          name: 'CreditTopupAdmin',
          component: () => import('../views/CreditTopupAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin'] }
        },
        {
          path: '/entity-wallets',
          name: 'EntityWalletsAdmin',
          component: () => import('../views/EntityWalletsAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/pin-top',
          name: 'PinTopAdmin',
          component: () => import('../views/PinTopAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/push-broadcast',
          name: 'PushBroadcastAdmin',
          component: () => import('../views/PushBroadcastAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin'] }
        },
        {
          path: '/push-moderation',
          name: 'PushModerationAdmin',
          component: () => import('../views/PushModerationAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/banner-requests',
          name: 'BannerRequestsAdmin',
          component: () => import('../views/BannerRequestsAdminView.vue'),
          meta: { ui: 'v2', roles: ['super_admin', 'admin'] }
        },
        {
          path: '/business/home',
          name: 'LMenusOwnerHome',
          component: () => import('../views/LMenusOwnerHomeView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'], ownerLmenusHome: true }
        },
        {
          path: '/business/my-stats',
          name: 'MyRestaurantStats',
          component: () => import('../views/RestaurantOwnerStatsView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'], ownerRestaurantStats: true }
        },
        {
          path: '/business/my-orders',
          name: 'MyRestaurantOrders',
          component: () => import('../views/BusinessOwnerOrdersView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'], ownerRestaurantOrders: true }
        },
        {
          path: '/business/my-hatly-orders',
          name: 'MyEntityHatlyOrders',
          component: () => import('../views/EntityOwnerHatlyOrdersView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'], ownerHatlyOrders: true }
        },
        {
          path: '/business/my-menu',
          name: 'MyRestaurantMenu',
          component: () => import('../views/RestaurantItemsView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'], ownerRestaurantMenu: true }
        },
        {
          path: '/business/my-data',
          name: 'MyEntityData',
          component: () => import('../views/MyEntityDataView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'] }
        },
        {
          path: '/business/my-doctor',
          name: 'MyDoctorProfile',
          component: () => import('../views/DoctorView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'], ownerDoctorEdit: true }
        },
        {
          path: '/business/my-reviews',
          name: 'MyEntityReviews',
          component: () => import('../views/MyEntityReviewsView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'] }
        },
        {
          path: '/business/promo-images',
          name: 'PromoImages',
          component: () => import('../views/PromoImagesView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'] }
        },
        {
          path: '/business/my-offer',
          name: 'MyOffer',
          component: () => import('../views/MyOfferView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'] }
        },
        {
          path: '/business/credits-info',
          name: 'CreditsInfo',
          component: () => import('../views/CreditsInfoView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'] }
        },
        {
          path: '/business/lmenus-plan',
          name: 'LMenusOwnerPlan',
          component: () => import('../views/LMenusOwnerPlanView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'] }
        },
        {
          path: '/business/my-wallet',
          name: 'MyWallet',
          component: () => import('../views/MyWalletView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'] }
        },
        {
          path: '/business/pin-top',
          name: 'MyPinTop',
          component: () => import('../views/MyPinTopView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'] }
        },
        {
          path: '/business/push',
          name: 'MyPush',
          component: () => import('../views/MyPushView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'] }
        },
        {
          path: '/business/banner-requests',
          name: 'MyBannerRequests',
          component: () => import('../views/MyBannerRequestsView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'] }
        },
        {
          path: '/business/delivery-zones',
          name: 'EntityOwnerDeliveryZones',
          component: () => import('../views/EntityOwnerDeliveryZonesView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'], ownerSelfDelivery: true }
        },
        {
          path: '/business/captains',
          name: 'EntityOwnerCaptains',
          component: () => import('../views/EntityOwnerDeliveryMenView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'], ownerSelfDelivery: true }
        },
        {
          path: '/business/service-fee',
          name: 'EntityOwnerServiceFee',
          component: () => import('../views/EntityOwnerServiceFeeView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'], ownerSelfDelivery: true }
        },
        {
          path: '/business/hall-tables',
          name: 'EntityOwnerHallTables',
          component: () => import('../views/EntityOwnerHallTablesView.vue'),
          meta: { ui: 'v2', roles: ['business_owner'], ownerHallTables: true }
        },
        {
          path: '/business/pos/staff',
          name: 'PosStaff',
          component: () => import('../views/pos/PosStaffView.vue'),
          meta: { roles: ['business_owner'], ownerPos: true, ui: 'v2' }
        },
        {
          path: '/business/pos/settings',
          name: 'PosSettings',
          component: () => import('../views/pos/PosSettingsView.vue'),
          meta: { roles: ['business_owner'], ownerPos: true, ui: 'v2' }
        },
        {
          path: '/business/pos/reports',
          name: 'PosReports',
          component: () => import('../views/pos/PosReportsView.vue'),
          meta: { roles: ['business_owner'], ownerPos: true, ui: 'v2' }
        }
      ]
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('../views/NotFoundView.vue'),
      meta: { public: true, ui: 'v2' }
    }
  ]
})

function redirectOtherPublicProfilesToMenuSite(to) {
  if (isMenuPublicSite()) return false

  const path = String(to.path || '')
  const base = getPublicMenuBaseUrl()
  if (!base) return false

  for (const cfg of Object.values(OTHER_PUBLIC_ENTITY_CONFIGS)) {
    const prefix = `/${cfg.pathSegment}/`
    if (!path.startsWith(prefix)) continue
    const id = String(to.params.entityId || path.slice(prefix.length).split('/')[0] || '').trim()
    if (!id) return false
    window.location.replace(`${base}/${cfg.pathSegment}/${encodeURIComponent(id)}`)
    return true
  }

  return false
}

// Navigation guard
router.beforeEach(async (to, from, next) => {
  // مطعم / سوق — نفس إعادة التوجيه القديمة دون تغيير
  if (to.path.startsWith('/r/') && !isMenuPublicSite()) {
    const id = String(to.params.restaurantId || to.path.replace(/^\/r\//, '').split('/')[0] || '').trim()
    if (id) {
      const base = getPublicMenuBaseUrl()
      window.location.replace(`${base}/r/${encodeURIComponent(id)}`)
      return
    }
  }

  // LMenus slug links — حوّل للـ menu host لو فتحوا من لوحة الإدارة
  if (to.path.startsWith('/m/') && !isMenuPublicSite() && to.name === 'LMenusRestaurantPublic') {
    const slug = String(to.params.slug || to.path.replace(/^\/m\//, '').split('/')[0] || '').trim()
    if (slug) {
      const base = getLmenusPublicBaseUrl()
      window.location.replace(`${base}/m/${encodeURIComponent(slug)}`)
      return
    }
  }

  if (redirectOtherPublicProfilesToMenuSite(to)) {
    return
  }

  if (isMenuPublicSite()) {
    // Gusto!'s own address opens Gusto!, not Madentak's home page.
    if (to.path === '/' && isGustoHost()) {
      next('/gusto')
      return
    }
    // لوجين LMenus لازم على دومين الأدمن (نفس أصل الجلسة)
    if (to.path === '/gusto/login') {
      window.location.replace(getLmenusLoginUrl())
      return
    }
    // Dashboard / POS links opened on the menu host → the admin host
    // /gusto-admin is checked here because the /gusto pages below are public.
    if (
      to.path === '/login' ||
      to.path.startsWith('/pos') ||
      to.path.startsWith('/business') ||
      to.path === '/gusto-admin' ||
      to.path === '/lmenus-admin'
    ) {
      const base = getAdminBaseUrl()
      if (base) {
        window.location.replace(`${base}${to.fullPath}`)
        return
      }
    }
    if (
      to.meta.public === true ||
      to.path.startsWith('/r/') ||
      to.path.startsWith('/m/') ||
      to.path.startsWith('/gusto')
    ) {
      next()
      return
    }
    next('/')
    return
  }

  const userRole = localStorage.getItem('role') || ''
  const home = homeFor(userRole)

  // Root (and the menu-site home on the admin host): the account's home, or login.
  if (to.path === '/' || to.meta.menuSiteHome === true) {
    next(home || (isGustoHost() ? '/gusto/login' : '/login'))
    return
  }

  // Login pages: someone already signed in goes straight to their home.
  if (to.path === '/login' || to.path === '/gusto/login' || to.path === '/pos/login') {
    if (userRole && home) {
      const check = await validatePanelSession(userRole).catch(() => ({ ok: false }))
      if (check.ok) {
        next(home)
        return
      }
      clearPanelSession()
    }
    next()
    return
  }

  if (to.meta.public === true || to.path === '/privacypolicy') {
    next()
    return
  }

  // Not signed in
  if (!userRole) {
    if (to.path.startsWith('/pos')) next('/pos/login')
    else if ((to.path.startsWith('/gusto') && to.path !== '/gusto-admin') || to.path === '/business/home') next('/gusto/login')
    else next('/login')
    return
  }

  // Unknown role (old session format) → sign in again
  if (!home) {
    clearPanelSession()
    next('/login')
    return
  }

  const sessionCheck = await validatePanelSession(userRole).catch((e) => {
    console.error('[panelSession]', e)
    return { ok: false }
  })
  if (!sessionCheck.ok) {
    const login = loginFor(userRole)
    clearPanelSession()
    next(login)
    return
  }

  // POS: staff only ever see POS screens; each screen needs its permission.
  if (userRole === 'pos_staff' && !to.meta?.pos) {
    next(home)
    return
  }
  if (to.meta?.pos) {
    const posRole = currentPosRole()
    if (!posRole) {
      if (userRole === 'pos_staff') {
        clearPanelSession()
        next('/pos/login')
      } else {
        next(home)
      }
      return
    }
    if (!posCan(to.meta.posPerm, posRole)) {
      const posHome = posHomePath(posRole)
      if (posHome === to.path) {
        // the role can't even open its own home screen → start over
        const login = loginFor(userRole)
        clearPanelSession()
        next(login)
      } else {
        next(posHome)
      }
      return
    }
    next()
    return
  }
  if (to.meta?.ownerPos && userRole === 'business_owner') {
    if (localStorage.getItem('entityType') !== 'restaurants' || localStorage.getItem('posEnabled') !== 'true') {
      next(ownerHome())
      return
    }
  }

  // LMenus owners: block Madentk-only business pages
  const isLmenusOwner =
    userRole === 'business_owner' &&
    (localStorage.getItem('lmenus') === 'true' || localStorage.getItem('city') === 'virtual')
  if (isLmenusOwner) {
    // مناطق التوصيل + الكباتن مسموحة لـ LMenus (self-delivery)
    // رسوم التطبيق محظورة — الاشتراك بالباقة بدل رسوم الطلب
    const madentkOnly = [
      '/business/credits-info',
      '/business/my-wallet',
      '/business/pin-top',
      '/business/push',
      '/business/banner-requests',
      '/business/promo-images',
      '/business/my-offer',
      '/business/my-reviews',
      '/business/service-fee',
      '/business/my-hatly-orders',
      '/business/my-doctor',
      '/business/my-stats'
    ]
    if (madentkOnly.some((p) => to.path === p || to.path.startsWith(p + '/'))) {
      next('/business/home')
      return
    }
  } else if (userRole === 'business_owner' && (to.path === '/business/lmenus-plan' || to.path === '/business/home')) {
    next(to.path === '/business/home' ? '/business/my-data' : '/business/credits-info')
    return
  }

  if (
    (to.meta?.ownerRestaurantMenu ||
      to.meta?.ownerRestaurantStats ||
      to.meta?.ownerRestaurantOrders ||
      to.meta?.ownerLmenusHome) &&
    userRole === 'business_owner'
  ) {
    const et = localStorage.getItem('entityType') || ''
    if (to.meta?.ownerLmenusHome) {
      const lmenus =
        localStorage.getItem('lmenus') === 'true' || localStorage.getItem('city') === 'virtual'
      if (!lmenus || et !== 'restaurants') {
        next(ownerHome())
        return
      }
    }
    if (to.meta?.ownerRestaurantStats && et !== 'restaurants') {
      next(ownerHome())
      return
    }
    if (
      (to.meta?.ownerRestaurantMenu || to.meta?.ownerRestaurantOrders) &&
      et !== 'restaurants' &&
      et !== 'markets'
    ) {
      next(ownerHome())
      return
    }
    if (!localStorage.getItem('entityId')) {
      next(ownerHome())
      return
    }
  }

  if (to.meta?.ownerHatlyOrders && userRole === 'business_owner') {
    const et = localStorage.getItem('entityType') || ''
    if (et !== 'pharmacies' && et !== 'shops' && et !== 'labs') {
      next(ownerHome())
      return
    }
    if (!localStorage.getItem('entityId')) {
      next(ownerHome())
      return
    }
  }

  if (to.meta?.ownerSelfDelivery && userRole === 'business_owner') {
    if (localStorage.getItem('deliveryByOffice') !== 'false') {
      next(ownerHome())
      return
    }
    if (!localStorage.getItem('entityId')) {
      next(ownerHome())
      return
    }
  }

  if (to.meta?.ownerHallTables && userRole === 'business_owner') {
    const et = localStorage.getItem('entityType') || ''
    if (et !== 'restaurants' || localStorage.getItem('hallOrders') !== 'true') {
      next(ownerHome())
      return
    }
    if (!localStorage.getItem('entityId')) {
      next(ownerHome())
      return
    }
  }

  if (to.meta.roles) {
    if (to.meta.roles.includes(userRole)) {
      next()
    } else if (userRole === 'business_owner') {
      next(ownerHome())
    } else {
      next('/Order')
    }
  } else {
    next()
  }
})

// Only the Gusto! marketing pages (landing, register, login) are reported to
// Facebook; restaurants' own sites and everything behind a login are not.
router.afterEach((to) => {
  if (!to.meta.lmenusMarketing) return
  // index.html already counted the page the visitor arrived on.
  if (window.__lmPageViewSent) {
    window.__lmPageViewSent = false
    return
  }
  trackPixel('PageView')
})

export default router
